import type { To, Transition } from 'history'
import { parsePath } from 'history'
import { useEffect, useRef } from 'react'
import { useLocation } from 'react-router'

import { CustomEvent } from '../enums/customEvent'
import { BrowserBackButtonBlockRequest } from '../types/BrowserBackButtonBlockRequest'
import { dispatchCustomEvent } from '../utils/customEvent'
import { getIsURLChangeStartedByEmberRouter, history } from '../utils/history'
import { useEmberTransitionTo } from './routing/useEmberTransitionTo'
import { CustomEventType, useCustomEventListener } from './useCustomEventListener'

const blockedBySources = new Set<string>()

export const useReactEmberRouterConnection = () => {
  const { replaceWith, transitionTo } = useEmberTransitionTo()
  const location = useLocation()
  const currentLocationKey = useRef(location.key)
  const previousLocationKey = useRef<string | undefined | null>(null)

  useEffect(() => {
    previousLocationKey.current = currentLocationKey.current
    currentLocationKey.current = location.key
  }, [location.key])

  const previousLocation = useRef(location.pathname)
  const shouldBlockBackButtonClick = useRef(false)
  // Set inside the blocking effect below - lets this dirty-flag listener arm/disarm history.block()
  // the moment the flag changes, instead of only reacting once some later transition happens to fire.
  const syncBlockRegistrationRef = useRef<() => void>(() => undefined)

  useEffect(() => {
    previousLocation.current = location.pathname
  }, [location.pathname])

  useCustomEventListener<BrowserBackButtonBlockRequest>(
    CustomEvent.BlockBrowserBackButtonRequested,
    (event: CustomEventType<BrowserBackButtonBlockRequest>) => {
      if (!event.detail) {
        return
      }

      const { sourceId: blockerId, value: shouldBlock } = event.detail

      if (!blockerId) {
        return
      }

      if (shouldBlock) {
        blockedBySources.add(blockerId)
      } else {
        blockedBySources.delete(blockerId)
      }

      shouldBlockBackButtonClick.current = blockedBySources.size > 0

      syncBlockRegistrationRef.current()
    },
  )

  // Ember's own native popstate listener runs independently of history's block/retry dance (see
  // Ember's HistoryLocation.onUpdateURL) and can fire multiple times mid-transition while a
  // browser back/forward is being processed. If our tracked location ends up out of sync with the
  // real URL once Ember has settled, force a resync so the rendered page follows the address bar.
  useCustomEventListener(CustomEvent.EmberRouteSettled, () => {
    const trueUrl = `${window.location.pathname}${window.location.search}${window.location.hash}`
    const trackedUrl = `${history.location.pathname}${history.location.search}${history.location.hash}`

    if (trueUrl !== trackedUrl) {
      history.replace(trueUrl)
    }
  })

  // history.block() (below) forces EVERY browser Back/Forward through a revert-then-ask round trip
  // as soon as ANY blocker is registered, even ones that end up allowing the transition instantly -
  // confirmed in history's own source (createBrowserHistory's handlePop: `if (blockers.length)`
  // reverts unconditionally, before the blocker function is ever consulted). Since PUSH/REPLACE
  // never needed that revert (history only asks blockers BEFORE touching the real browser history,
  // no physical back-and-forth), intercepting push/replace here - before they ever reach history's
  // own blocker check - lets history.block() be registered ONLY while a dirty form genuinely needs
  // to guard Back (see the effect below), instead of for the app's entire lifetime.
  useEffect(() => {
    const originalPush = history.push
    const originalReplace = history.replace

    const getPathname = (to: To): string =>
      typeof to === 'string' ? (parsePath(to).pathname ?? '/') : (to.pathname ?? '/')

    const syncThroughEmber = (to: To, action: 'PUSH' | 'REPLACE') => {
      // when we change url more than once quickly
      // for example by going to a view that triggers redirect we might end up with a empty Ember view
      // moving transition to the next frame fixes this
      requestAnimationFrame(() => {
        const url = history.createHref(to)

        if (action === 'REPLACE') {
          replaceWith(url)
        } else {
          transitionTo(url)
        }
      })
    }

    history.push = (to, state) => {
      if (!getIsURLChangeStartedByEmberRouter() && getPathname(to) !== window.location.pathname) {
        syncThroughEmber(to, 'PUSH')
        return
      }

      originalPush(to, state)
    }

    history.replace = (to, state) => {
      if (!getIsURLChangeStartedByEmberRouter() && getPathname(to) !== window.location.pathname) {
        syncThroughEmber(to, 'REPLACE')
        return
      }

      originalReplace(to, state)
    }

    return () => {
      history.push = originalPush
      history.replace = originalReplace
    }
  }, [replaceWith, transitionTo])

  useEffect(() => {
    // Only Ember's dirty-route confirmation (branch below) genuinely needs to intercept a browser
    // Back/Forward before it commits - PUSH/REPLACE's Ember-sync is handled above without blocking.
    // So keep history.block() registered only while a dirty form/route genuinely needs it, instead
    // of for the whole session - ordinary Back/Forward (the common case) then never pays for a
    // revert it didn't need. The blocker function itself is unchanged from before: it still has to
    // cope with PUSH/REPLACE reaching it too, on the rare occasion one slips through while a blocker
    // is already registered for an unrelated dirty form elsewhere.
    let unblock: () => void = () => undefined
    let isBlocked = false
    let unlistenPendingPop: () => void = () => undefined

    const syncBlockRegistration = () => {
      const shouldBeBlocked = blockedBySources.size > 0

      if (shouldBeBlocked && !isBlocked) {
        unblock = history.block(blocker)
        isBlocked = true
      } else if (!shouldBeBlocked && isBlocked) {
        unblock()
        isBlocked = false
      }
    }

    syncBlockRegistrationRef.current = syncBlockRegistration

    const allowPushOrReplace = (tx: Transition) => {
      try {
        unblock()
        isBlocked = false
        tx.retry()
      } finally {
        syncBlockRegistration()
      }
    }

    const allowPop = (tx: Transition) => {
      unblock()
      isBlocked = false
      unlistenPendingPop()
      unlistenPendingPop = history.listen(() => {
        unlistenPendingPop()
        unlistenPendingPop = () => undefined
        syncBlockRegistration()
      })

      try {
        tx.retry()
      } catch (error) {
        unlistenPendingPop()
        unlistenPendingPop = () => undefined
        syncBlockRegistration()
        throw error
      }
    }

    const blocker = (tx: Transition) => {
      const isURLChangeStartedByEmberRouter = getIsURLChangeStartedByEmberRouter()
      const addressChangeByBackButtonClick = tx.action === 'POP' && tx.location.pathname !== previousLocation.current
      const addressChangeByLinkClickOrFunction =
        tx.action !== 'POP' && tx.location.pathname !== window.location.pathname

      if (isURLChangeStartedByEmberRouter) {
        if (tx.action === 'POP') {
          allowPop(tx)
        } else {
          allowPushOrReplace(tx)
        }
        return
      }

      if (shouldBlockBackButtonClick.current && addressChangeByBackButtonClick) {
        dispatchCustomEvent<boolean>(
          CustomEvent.BrowserNavigationButtonClickedAndBlocked,
          tx.location.key === previousLocationKey.current,
        )
        return
      }

      if (tx.action === 'POP') {
        allowPop(tx)
        return
      }

      if (addressChangeByLinkClickOrFunction) {
        // when we change url more than once quickly
        // for example by going to a view that triggers redirect we might end up with a empty Ember view
        // moving transition to the next frame fixes this
        requestAnimationFrame(() => {
          const url = `${tx.location.pathname}${tx.location.search}${tx.location.hash}`

          if (tx.action === 'REPLACE') {
            replaceWith(url)
          } else {
            transitionTo(url)
          }
        })
        return
      }

      allowPushOrReplace(tx)
    }

    syncBlockRegistration()

    return () => {
      syncBlockRegistrationRef.current = () => undefined
      unlistenPendingPop()
      unblock()
    }
  }, [replaceWith, transitionTo])
}
